import React from 'react'
import {type AppBskyFeedDefs} from '@atproto/api'
import {useNavigation} from '@react-navigation/native'
import clsx from 'clsx'

import {type NavigationProp} from '#/lib/routes/types'
import {web} from '#/alf'
import GalleryMedia from '#/components/feed/GalleryMedia'
import {Mute_Stroke2_Corner0_Rounded as MuteIcon} from '#/components/icons/Mute'
import {SpeakerVolumeFull_Stroke2_Corner0_Rounded as SpeakerIcon} from '#/components/icons/Speaker'
import {Text} from '#/components/Typography'
import PostActions from '../components/PostActions'
import ClipsCommentsDrawer from '../components/reels/ClipsCommentsDrawer'
import ClipsPager from '../components/reels/ClipsPager'
import SaveToCollectionSheet from '../components/SaveToCollectionSheet'
import VideoReel from '../components/VideoReel'
import {getCounts, getPostImages} from '../lib/feed/mediaHelpers'
import {getPrimaryMedia} from '../lib/media'
import {type SavedFeedSourceInfo} from '../state/queries/feed'
import {type FeedDescriptor} from '../state/queries/post-feed'
import {useSavedCollections} from '../state/savedCollections'
import {useAgent} from '../state/session'

const PAGE_SIZE = 30
const CLIPS_SOUND_PREF_KEY = 'clips:sound'

function readClipsSoundPref(): boolean | null {
  if (typeof window === 'undefined') return null
  try {
    const value = window.localStorage.getItem(CLIPS_SOUND_PREF_KEY)
    if (value === '1') return true
    if (value === '0') return false
  } catch {
    return null
  }
  return null
}

function writeClipsSoundPref(value: boolean) {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(CLIPS_SOUND_PREF_KEY, value ? '1' : '0')
  } catch {
    // Ignore storage errors (private mode, blocked storage).
  }
}

function useClipsSoundPreference() {
  const initialPrefRef = React.useRef<boolean | null>()
  if (initialPrefRef.current === undefined) {
    initialPrefRef.current = readClipsSoundPref()
  }
  const initialPref = initialPrefRef.current
  const [soundEnabled, setSoundEnabled] = React.useState(initialPref ?? false)
  const [soundPrefSet, setSoundPrefSet] = React.useState(initialPref !== null)
  const [hasUserGesture, setHasUserGesture] = React.useState(false)

  const updateSoundPref = React.useCallback(
    (next: boolean, fromUserGesture = false) => {
      setSoundEnabled(next)
      setSoundPrefSet(true)
      if (fromUserGesture) {
        setHasUserGesture(true)
      }
      writeClipsSoundPref(next)
    },
    [],
  )

  const markUserGesture = React.useCallback(() => {
    setHasUserGesture(true)
  }, [])

  return {
    soundEnabled,
    soundPrefSet,
    hasUserGesture,
    setSoundEnabled: updateSoundPref,
    markUserGesture,
  }
}

type ReactionState = {liked: boolean; saved: boolean; likeUri?: string}
type CountState = {likes: number; comments: number}

type ActiveSaveState = {key: string; uri: string; fallback: ReactionState}
type ActiveCommentsState = {
  key: string
  post: AppBskyFeedDefs.PostView
  counts: CountState
}

type LayoutMode = 'gallery' | 'tiktok'

type HomeFeedProps = {
  feedDescriptor: FeedDescriptor
  feedInfo?: SavedFeedSourceInfo
  layout: LayoutMode
}

const isDev =
  typeof __DEV__ !== 'undefined'
    ? __DEV__
    : process.env.NODE_ENV !== 'production'

export default function HomeFeed({
  feedDescriptor,
  feedInfo,
  layout,
}: HomeFeedProps) {
  const agent = useAgent()
  const [feedItems, setFeedItems] = React.useState<
    AppBskyFeedDefs.FeedViewPost[]
  >([])
  const [userReactions, setUserReactions] = React.useState<
    Record<string, ReactionState>
  >({})
  const [countsByKey, setCountsByKey] = React.useState<
    Record<string, CountState>
  >({})
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [activeSave, setActiveSave] = React.useState<ActiveSaveState | null>(
    null,
  )
  const [activeComments, setActiveComments] =
    React.useState<ActiveCommentsState | null>(null)
  const {
    soundEnabled: clipsSoundEnabled,
    soundPrefSet: clipsSoundPrefSet,
    hasUserGesture: clipsHasUserGesture,
    setSoundEnabled: setClipsSoundEnabled,
    markUserGesture: markClipsUserGesture,
  } = useClipsSoundPreference()

  const cursorRef = React.useRef<string | undefined>()
  const navigation = useNavigation<NavigationProp>()

  const posts = React.useMemo(() => {
    return feedItems
      .map(item => item.post)
      .filter((post): post is AppBskyFeedDefs.PostView => Boolean(post))
  }, [feedItems])

  const reelItems = React.useMemo(() => {
    const items: Array<{
      key: string
      post: AppBskyFeedDefs.PostView
      mediaUrl: string
      poster?: string
    }> = []
    feedItems.forEach((item, index) => {
      const clip = getClipMedia(item)
      if (!clip) return
      const key = clip.post.uri ?? (item as any).uri ?? `reel-${index}`
      items.push({
        key,
        post: clip.post,
        mediaUrl: clip.mediaUrl,
        poster: clip.poster,
      })
    })
    return items
  }, [feedItems])

  const hasMore = Boolean(cursorRef.current)

  const ensureReaction = React.useCallback(
    (post: AppBskyFeedDefs.PostView, key: string) => {
      return (
        userReactions[key] ?? {
          liked: Boolean(post.viewer?.like),
          likeUri: post.viewer?.like ?? undefined,
          saved: Boolean(post.viewer?.bookmarked),
        }
      )
    },
    [userReactions],
  )

  const updateReaction = React.useCallback(
    (
      key: string,
      fallback: ReactionState,
      updater: (current: ReactionState) => ReactionState,
    ) => {
      setUserReactions(prev => {
        const current = prev[key] ?? fallback
        const next = updater(current)
        return {...prev, [key]: next}
      })
    },
    [],
  )

  const updateCounts = React.useCallback(
    (
      key: string,
      fallback: CountState,
      updater: (current: CountState) => CountState,
    ) => {
      setCountsByKey(prev => {
        const current = prev[key] ?? fallback
        const next = updater(current)
        return {...prev, [key]: next}
      })
    },
    [],
  )

  const fetchFeed = React.useCallback(
    async (
      cursor?: string,
    ): Promise<{feed: AppBskyFeedDefs.FeedViewPost[]; cursor?: string}> => {
      try {
        if (feedDescriptor === 'following') {
          const res = await agent.getTimeline({cursor, limit: PAGE_SIZE})
          if (res.success) {
            return {feed: res.data.feed ?? [], cursor: res.data.cursor}
          }
          return {feed: [], cursor: undefined}
        }

        const descriptorParts = feedDescriptor.split('|')
        const descriptorType = descriptorParts[0]
        const descriptorUri = descriptorParts[1] ?? feedInfo?.uri

        if (descriptorType === 'list' && descriptorUri) {
          const res = await agent.app.bsky.feed.getListFeed({
            list: descriptorUri,
            cursor,
            limit: PAGE_SIZE,
          })
          return {
            feed: res.data.feed ?? [],
            cursor: res.data.cursor ?? undefined,
          }
        }

        const feedUri = descriptorUri ?? feedInfo?.uri
        if (!feedUri) {
          return {feed: [], cursor: undefined}
        }

        const res = await agent.app.bsky.feed.getFeed({
          feed: feedUri,
          cursor,
          limit: PAGE_SIZE,
        })
        return {
          feed: res.data.feed ?? [],
          cursor: res.data.cursor ?? undefined,
        }
      } catch (err) {
        console.error('[HomeFeed] fetch error', err)
        throw err
      }
    },
    [agent, feedDescriptor, feedInfo?.uri],
  )

  React.useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)
    cursorRef.current = undefined
    fetchFeed()
      .then(({feed, cursor}) => {
        if (cancelled) return
        setFeedItems(feed)
        cursorRef.current = cursor
      })
      .catch(err => {
        if (cancelled) return
        setError(err instanceof Error ? err.message : 'Erreur inconnue')
        setFeedItems([])
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [fetchFeed])

  const loadMore = React.useCallback(async () => {
    const cursor = cursorRef.current
    if (!cursor) return
    try {
      const {feed, cursor: nextCursor} = await fetchFeed(cursor)
      setFeedItems(prev => [...prev, ...feed])
      cursorRef.current = nextCursor
    } catch (err) {
      console.error('[HomeFeed] pagination error', err)
    }
  }, [fetchFeed])

  const openDetail = React.useCallback(
    (post: AppBskyFeedDefs.PostView) => {
      if (!post?.uri) return
      navigation.navigate('GalleryPost', {uri: post.uri})
    },
    [navigation],
  )

  const closeComments = React.useCallback(() => {
    setActiveComments(null)
  }, [])

  const handleCommentCountChange = React.useCallback(
    (delta: number) => {
      if (!activeComments) return
      updateCounts(activeComments.key, activeComments.counts, current => ({
        ...current,
        comments: Math.max(0, current.comments + delta),
      }))
    },
    [activeComments, updateCounts],
  )

  const handleCloseSaveSheet = React.useCallback(() => {
    setActiveSave(prev => {
      if (prev) {
        const membership = useSavedCollections
          .getState()
          .contains(prev.uri).inAny
        updateReaction(prev.key, prev.fallback, current => ({
          ...current,
          saved: membership,
        }))
      }
      return null
    })
  }, [updateReaction])

  const toggleLike = React.useCallback(
    async (
      post: AppBskyFeedDefs.PostView,
      key: string,
      reaction: ReactionState,
      counts: CountState,
    ) => {
      if (!post.uri || !post.cid) return
      const previousReaction = reaction
      const previousCounts = counts
      const nextLiked = !reaction.liked

      updateReaction(key, reaction, current => ({
        ...current,
        liked: nextLiked,
        likeUri: nextLiked ? current.likeUri : undefined,
      }))
      updateCounts(key, counts, current => ({
        ...current,
        likes: Math.max(0, current.likes + (nextLiked ? 1 : -1)),
      }))

      try {
        if (nextLiked) {
          const res = await agent.like(post.uri, post.cid)
          setUserReactions(prev => ({
            ...prev,
            [key]: {
              ...(prev[key] ?? reaction),
              liked: true,
              likeUri: res.uri,
            },
          }))
        } else {
          const likeUri = reaction.likeUri ?? post.viewer?.like
          if (likeUri) {
            await agent.deleteLike(likeUri)
          }
          setUserReactions(prev => ({
            ...prev,
            [key]: {
              ...(prev[key] ?? reaction),
              liked: false,
              likeUri: undefined,
            },
          }))
        }
      } catch (err) {
        console.error('[HomeFeed] like error', err)
        setUserReactions(prev => ({...prev, [key]: previousReaction}))
        setCountsByKey(prev => ({...prev, [key]: previousCounts}))
      }
    },
    [agent, updateCounts, updateReaction],
  )

  const sharePost = React.useCallback((post: AppBskyFeedDefs.PostView) => {
    const url = `https://tellus.app/profile/${post.author?.handle}/post/${post.uri.split('/').pop()}`
    if (typeof navigator !== 'undefined' && navigator.share) {
      navigator.share({url}).catch(() => {
        if (typeof window !== 'undefined') window.open(url, '_blank')
      })
    } else if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {})
    } else if (typeof window !== 'undefined') {
      window.open(url, '_blank')
    }
  }, [])

  const renderPost = React.useCallback(
    (post: AppBskyFeedDefs.PostView, index: number) => {
      const key = post.uri ?? `post-${index}`
      const baseReaction = ensureReaction(post, key)
      const reaction = userReactions[key] ?? baseReaction
      const countsRaw = getCounts(post as any)
      const countsFromPost: CountState = {
        likes: countsRaw.like ?? post.likeCount ?? 0,
        comments: countsRaw.comment ?? post.replyCount ?? 0,
      }
      const counts = countsByKey[key] ?? countsFromPost

      const record = post.record as {text?: string} | undefined
      const text = record?.text ?? ''
      const authorName =
        post.author?.displayName || post.author?.handle || 'Inconnu'
      const authorHandle = post.author?.handle
      const createdAt = post.indexedAt ? new Date(post.indexedAt) : null
      const images = getPostImages(post)

      const onLike = () => toggleLike(post, key, reaction, counts)
      const onSave = () => {
        if (!post.uri) return
        setActiveSave({key, uri: post.uri, fallback: reaction})
      }
      const onComment = () => openDetail(post)
      const onShare = () => sharePost(post)

      if (layout === 'gallery') {
        if (images.length === 0) {
          return null
        }
        return (
          <InstagramPostCard
            key={key}
            authorName={authorName}
            authorHandle={authorHandle}
            avatarUrl={post.author?.avatar ?? undefined}
            createdAt={createdAt}
            images={images}
            text={text}
            reaction={reaction}
            counts={counts}
            onLike={onLike}
            onSave={onSave}
            onComment={onComment}
            onShare={onShare}
            onOpen={() => openDetail(post)}
          />
        )
      }

      return null
    },
    [
      countsByKey,
      ensureReaction,
      layout,
      openDetail,
      sharePost,
      setActiveSave,
      toggleLike,
      userReactions,
    ],
  )

  const renderReelsLayout = React.useCallback(() => {
    if (isLoading && reelItems.length === 0)
      return (
        <div className="home-feed-loading">
          <Text style={{fontSize: 15, color: 'inherit'}}>Chargement…</Text>
        </div>
      )
    if (reelItems.length === 0) {
      return (
        <div
          className="home-feed-stream layout-tiktok"
          style={{padding: 24, textAlign: 'center'}}>
          <Text style={{fontSize: 16, color: 'inherit'}}>
            Aucune vidéo trouvée dans ce feed.
          </Text>
        </div>
      )
    }

    return (
      <ClipsPager
        items={reelItems}
        className="home-feed-scroll clipsScroller"
        getKey={item => item.key}
        renderItem={({item, index, activeIndex}) => {
          const {post, key, mediaUrl, poster} = item

          const baseReaction = ensureReaction(post, key)
          const reaction = userReactions[key] ?? baseReaction
          const countsRaw = getCounts(post as any)
          const countsFromPost: CountState = {
            likes: countsRaw.like ?? post.likeCount ?? 0,
            comments: countsRaw.comment ?? post.replyCount ?? 0,
          }
          const counts = countsByKey[key] ?? countsFromPost

          const onLike = () => toggleLike(post, key, reaction, counts)
          const onSave = () => {
            if (!post.uri) return
            setActiveSave({key, uri: post.uri, fallback: reaction})
          }
          const onComment = () => setActiveComments({key, post, counts})
          const onShare = () => sharePost(post)

          return (
            <ClipItem
              index={index}
              activeIndex={activeIndex}
              post={post}
              mediaUrl={mediaUrl}
              poster={poster}
              reaction={reaction}
              counts={counts}
              onLike={onLike}
              onSave={onSave}
              onComment={onComment}
              onShare={onShare}
              soundEnabled={clipsSoundEnabled}
              soundPrefSet={clipsSoundPrefSet}
              hasUserGesture={clipsHasUserGesture}
              onSoundChange={setClipsSoundEnabled}
              onUserGesture={markClipsUserGesture}
            />
          )
        }}
        footer={
          hasMore ? (
            <button className="reels-load-more" onClick={loadMore}>
              <Text style={{fontSize: 16, color: 'inherit'}}>Charger plus</Text>
            </button>
          ) : null
        }
      />
    )
  }, [
    clipsHasUserGesture,
    clipsSoundEnabled,
    clipsSoundPrefSet,
    countsByKey,
    ensureReaction,
    hasMore,
    isLoading,
    loadMore,
    markClipsUserGesture,
    reelItems,
    setActiveComments,
    setActiveSave,
    setClipsSoundEnabled,
    sharePost,
    toggleLike,
    userReactions,
  ])

  return (
    <div
      style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
      <div className={clsx('home-feed-wrapper', `layout-${layout}`)}>
        {error && (
          <div className="home-feed-error">
            <Text style={{fontSize: 14, color: 'inherit'}}>
              Erreur de chargement : {error}
            </Text>
          </div>
        )}

        {layout === 'tiktok' ? (
          renderReelsLayout()
        ) : isLoading && posts.length === 0 ? (
          <div className="home-feed-loading">
            <Text style={{fontSize: 15, color: 'inherit'}}>Chargement…</Text>
          </div>
        ) : (
          <div
            className={clsx('home-feed-stream', `layout-${layout}`)}
            data-feed-scroll="">
            {posts.map((post, index) => renderPost(post, index))}
          </div>
        )}

        {hasMore && layout !== 'tiktok' && (
          <div className="home-feed-more">
            <button onClick={loadMore}>
              <Text style={{fontSize: 16, color: 'inherit'}}>Charger plus</Text>
            </button>
          </div>
        )}
      </div>

      <ClipsCommentsDrawer
        open={Boolean(activeComments)}
        post={activeComments?.post ?? null}
        onClose={closeComments}
        onCommentCountChange={handleCommentCountChange}
      />
      {activeSave && (
        <SaveToCollectionSheet
          atUri={activeSave.uri}
          onClose={handleCloseSaveSheet}
        />
      )}
    </div>
  )
}

type InstagramPostCardProps = {
  authorName: string
  authorHandle?: string
  avatarUrl?: string
  createdAt: Date | null
  images: Array<{url: string}>
  text: string
  reaction: ReactionState
  counts: CountState
  onLike: () => void
  onSave: () => void
  onComment: () => void
  onShare: () => void
  onOpen: () => void
}

function InstagramPostCard({
  authorName,
  authorHandle,
  avatarUrl,
  createdAt,
  images,
  text,
  reaction,
  counts,
  onLike,
  onSave,
  onComment,
  onShare,
  onOpen,
}: InstagramPostCardProps) {
  const likeCount = counts?.likes ?? 0
  const likesLabel = likeCount === 1 ? '1 like' : `${likeCount} likes`

  return (
    <article className="insta-card" onClick={onOpen}>
      <div className="insta-header">
        <div
          className="insta-avatar"
          style={
            avatarUrl ? {backgroundImage: `url(${avatarUrl})`} : undefined
          }>
          {!avatarUrl && (
            <Text style={{fontSize: 16, color: 'inherit'}}>
              {authorName[0]?.toUpperCase() ?? '•'}
            </Text>
          )}
        </div>
        <div className="insta-header-text">
          <Text style={{fontSize: 15, fontWeight: '600'}}>{authorName}</Text>
          {authorHandle && (
            <Text style={{fontSize: 12, opacity: 0.6}}>@{authorHandle}</Text>
          )}
        </div>
        <button
          type="button"
          className="insta-menu"
          aria-label="Options"
          onClick={event => event.stopPropagation()}>
          <Text style={{fontSize: 20}}>•••</Text>
        </button>
      </div>
      <GalleryMedia images={images} />
      <div className="insta-actions" onClick={event => event.stopPropagation()}>
        <PostActions
          layout="gallery"
          onLike={onLike}
          onComment={onComment}
          onSave={onSave}
          onShare={onShare}
          liked={reaction.liked}
          saved={reaction.saved}
          counts={counts}
        />
      </div>
      <div className="insta-meta">
        <div className="insta-likes">
          <Text style={{fontSize: 14, fontWeight: '600'}}>{likesLabel}</Text>
        </div>
        {text && (
          <Text
            style={[
              {fontSize: 14, lineHeight: 1.45},
              web({
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
            ]}>
            <Text style={{fontWeight: '600'}}>{authorName}</Text>{' '}
            <Text>{text}</Text>
          </Text>
        )}
        {createdAt && (
          <Text style={{fontSize: 12, opacity: 0.6}}>
            {createdAt.toLocaleDateString()}
          </Text>
        )}
      </div>
    </article>
  )
}

function getClipMedia(item: any): {
  post: AppBskyFeedDefs.PostView
  mediaUrl: string
  poster?: string
} | null {
  const post = item?.post ?? item
  if (!post) return null
  const media = getPrimaryMedia(item)
  const directVideo = collectVideoUrl(post)
  const mediaUrl =
    directVideo ?? (media?.type === 'video' ? media.url : undefined)
  if (!mediaUrl) return null
  const poster =
    (media?.type === 'video' ? media.poster : undefined) ??
    (media?.type === 'image' ? media.url : undefined)
  return {post, mediaUrl, poster}
}

function collectVideoUrl(post: any): string | undefined {
  const v =
    post?.embed?.video ||
    post?.record?.embed?.video ||
    post?.embed?.media?.video
  if (!v) return undefined
  return typeof v === 'string' ? v : v?.src || v?.url
}

function ClipItem({
  index,
  activeIndex,
  post,
  mediaUrl,
  poster,
  reaction,
  counts,
  onLike,
  onSave,
  onComment,
  onShare,
  soundEnabled,
  soundPrefSet,
  hasUserGesture,
  onSoundChange,
  onUserGesture,
}: {
  index: number
  activeIndex: number
  post: AppBskyFeedDefs.PostView
  mediaUrl: string
  poster?: string
  reaction: ReactionState
  counts: CountState
  onLike: () => void
  onSave: () => void
  onComment: () => void
  onShare: () => void
  soundEnabled: boolean
  soundPrefSet: boolean
  hasUserGesture: boolean
  onSoundChange: (enabled: boolean, fromUserGesture: boolean) => void
  onUserGesture: () => void
}) {
  React.useEffect(() => {
    if (!isDev) return
    console.debug('[Clips] item mount', {index, uri: post.uri})
    return () => {
      console.debug('[Clips] item unmount', {index, uri: post.uri})
    }
  }, [index, post.uri])

  return (
    <div className="reel-stage clipStage">
      <VideoReel
        className="reel-video"
        src={mediaUrl}
        poster={poster}
        active={index === activeIndex}
        index={index}
        activeIndex={activeIndex}
        soundEnabled={soundEnabled}
        soundPrefSet={soundPrefSet}
        hasUserGesture={hasUserGesture}
        onSoundChange={onSoundChange}
        onUserGesture={onUserGesture}
      />
      <div className="reel-actions">
        <PostActions
          layout="tiktok"
          onLike={onLike}
          onComment={onComment}
          onSave={onSave}
          onShare={onShare}
          liked={reaction.liked}
          saved={reaction.saved}
          counts={counts}
        />
        <button
          type="button"
          className="clip-sound-rail"
          aria-pressed={soundEnabled}
          aria-label={soundEnabled ? 'Couper le son' : 'Activer le son'}
          onClick={event => {
            event.stopPropagation()
            onSoundChange(!soundEnabled, true)
          }}>
          {soundEnabled ? (
            <SpeakerIcon size={22} fill="currentColor" />
          ) : (
            <MuteIcon size={22} fill="currentColor" />
          )}
        </button>
      </div>
    </div>
  )
}
